import { getDefaultAudioStream } from '#pplx/frontend/apps/ask/multimodal/media/microphone.js';
import { createSilenceMonitor } from '#pplx/frontend/apps/ask/multimodal/media/silenceMonitor.js';
import { getSonioxTranscriptionSession } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/api.js';
import { SONIOX_AUDIO_FORMAT, SONIOX_DICTATION_MODEL, SONIOX_MAX_ENDPOINT_DELAY_MS, SONIOX_NUM_CHANNELS, SONIOX_SAMPLE_RATE, SONIOX_SPECIAL_TOKENS, SONIOX_TURN_DETECTION_THRESHOLD, SONIOX_WEBSOCKET_URL, } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/consts.js';
import { IDLE_STATE, isTerminalState, } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/state.js';
import { defaultTimezone } from '#pplx/frontend/i18n/core.js';
import { logger } from '#pplx/frontend/logger/browser.js';
/**
 * One-shot speech recognition session for web dictation, mirroring the
 * Multimodal SDK's `SpeechRecognitionSession`: created, started once, discarded
 * once terminal. Each resource registers a disposer when acquired and `end()`
 * runs them all, once, before publishing the terminal state. Callbacks close
 * over the session and bail out once it is no longer live, so a late event from
 * an ended session cannot touch a newer one. `transcriptionStore.ts` owns the
 * current session and projects its state for React.
 */
/** Milliseconds after capture starts without a single PCM frame before the session fails. */
const NO_AUDIO_FRAMES_TIMEOUT_MS = 3000;
/** `getUserMedia` rejection names that mean the user or OS refused the microphone. */
const PERMISSION_ERROR_NAMES = new Set([
    'NotAllowedError',
    'PermissionDeniedError',
    'SecurityError',
]);
/** User and environment conditions, logged at `warn`; everything else is a defect-level `error`. */
const EXPECTED_FAILURES = new Set([
    'permissionDenied',
    'speechServicesBlocked',
    'webSpeech',
    'noAudio',
]);
const stopTracks = (stream) => stream.getTracks().forEach((track) => track.stop());
const joinSpeech = (parts) => parts.filter(Boolean).join(' ').trimStart();
const startFailed = (err) => ({
    kind: 'startFailed',
    message: String(err),
});
export const createSpeechRecognitionSession = (config) => {
    const { id } = config;
    let state = IDLE_STATE;
    /** Which recognizer feeds the session: a detail of the session, not a lifecycle state. */
    let engine = 'soniox';
    let startedAt = 0;
    let firstFrameAt = null;
    let framesSent = 0;
    /** Open Soniox socket; the worklet sends PCM on it. */
    let ws = null;
    /** Cleanups for everything the session owns, registered at acquisition and run in reverse. */
    const disposers = [];
    const own = (dispose) => {
        disposers.push(dispose);
    };
    const release = () => {
        while (disposers.length)
            disposers.pop()?.();
    };
    const alive = () => !isTerminalState(state);
    /** The only writer of `state`. Refused once terminal, mirroring the SDK's latch. */
    const transition = (next) => {
        if (isTerminalState(state))
            return;
        const prev = state;
        state = next;
        config.onStateChange(next, prev);
    };
    /** Terminal transition: releases first so a synchronous listener observes a clean slate. */
    const end = (next) => {
        if (!alive())
            return;
        logger.info('[transcription] session ended', {
            id,
            engine,
            endedIn: state.kind === 'active' ? state.phase : state.kind,
            cause: next.kind === 'failed' ? next.error.kind : next.cause,
            hasText: next.text.length > 0,
            framesSent,
            durationMs: Date.now() - startedAt,
        });
        release();
        transition(next);
    };
    /** Terminal states carry the last hypothesis shown, so nothing visible is lost on exit. */
    const lastText = () => (state.kind === 'active' ? state.partialText : '');
    const complete = (cause) => {
        end({ kind: 'completed', text: lastText(), cause });
    };
    /** Every failure path ends here, so this is the one log line per failed session. */
    const fail = (error) => {
        const level = EXPECTED_FAILURES.has(error.kind) ? 'warn' : 'error';
        logger[level]('[transcription] session failed', { id, engine, error });
        end({ kind: 'failed', error, text: lastText() });
    };
    /** `connecting` -> `active(waitingForSpeech)`; no-op in any other state. */
    const activate = () => {
        if (state.kind !== 'connecting')
            return;
        transition({ kind: 'active', phase: 'waitingForSpeech', finalizedText: '', partialText: '' });
    };
    /**
     * Engine swap: releases Soniox and capture, keeps the session and its state.
     * Returns `false` when Web Speech is unavailable; the caller then fails.
     */
    const startWebSpeech = (cause) => {
        const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
        if (!Ctor)
            return false;
        logger.warn('[transcription] Falling back to Web Speech API:', cause, { id });
        const base = state.kind === 'active' ? state.finalizedText : '';
        release();
        const recognition = new Ctor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event) => {
            if (!alive())
                return;
            const results = [...event.results];
            const text = (of) => joinSpeech([base, of.map((result) => result[0]?.transcript ?? '').join('')]);
            activate();
            if (state.kind !== 'active')
                return;
            transition({
                kind: 'active',
                phase: 'receivingSpeech',
                finalizedText: text(results.filter((result) => result.isFinal)),
                partialText: text(results),
            });
        };
        recognition.onerror = (event) => {
            if (!alive())
                return;
            fail(event.error === 'network'
                ? { kind: 'speechServicesBlocked' }
                : { kind: 'webSpeech', error: event.error });
        };
        // Chrome fires `end` after `error` (already terminal by then) and on its own silence timeout.
        recognition.onend = () => {
            if (alive())
                complete('recognizerEnded');
        };
        try {
            recognition.start();
        }
        catch (err) {
            logger.warn('[transcription] Web Speech start threw', { id, err });
            return false;
        }
        // Own the recognizer only once `start()` has succeeded.
        own(() => {
            recognition.onresult = recognition.onerror = recognition.onend = null;
            try {
                recognition.abort();
            }
            catch {
                // Aborting an already-ended recognizer is a no-op in spec but has thrown in some engines.
            }
        });
        engine = 'webSpeech';
        activate();
        return true;
    };
    const handleSonioxMessage = (raw) => {
        let data;
        try {
            data = JSON.parse(raw);
        }
        catch (err) {
            // Never log the frame, or the SyntaxError that quotes it: both may carry transcript text.
            logger.error('[soniox] Invalid WS message', {
                id,
                messageLength: raw.length,
                error: err instanceof Error ? err.name : typeof err,
            });
            return;
        }
        if (data.error_code !== undefined) {
            if (startWebSpeech(`error-${data.error_code}`))
                return;
            fail({ kind: 'provider', code: data.error_code, message: data.error_message });
            return;
        }
        if (!data.tokens?.length)
            return;
        // No-op guard: the first PCM frame activates the session before any audio reaches Soniox.
        activate();
        if (state.kind !== 'active')
            return;
        let { finalizedText } = state;
        let interim = '';
        for (const token of data.tokens) {
            if (SONIOX_SPECIAL_TOKENS.has(token.text))
                continue;
            if (token.is_final)
                finalizedText += token.text;
            else
                interim += token.text;
        }
        if (state.phase === 'waitingForSpeech') {
            logger.info('[soniox] first tokens', { id, msSinceStart: Date.now() - startedAt });
        }
        transition({
            kind: 'active',
            phase: 'receivingSpeech',
            finalizedText,
            partialText: (finalizedText + interim).trimStart(),
        });
    };
    /** Fetches a key and opens the socket. Returns failures instead of throwing them. */
    const connectSoniox = async () => {
        const sessionRes = await getSonioxTranscriptionSession({
            body: {
                source: config.source,
                timezone: defaultTimezone,
                turn_detection_threshold: SONIOX_TURN_DETECTION_THRESHOLD,
                response_language: 'en',
            },
            reason: config.reason,
        });
        if (!alive())
            return null;
        const apiKey = sessionRes.data?.api_key;
        if (!apiKey) {
            return { kind: 'sessionUnavailable', detail: sessionRes.error ?? 'missing api_key' };
        }
        const socket = new WebSocket(SONIOX_WEBSOCKET_URL);
        ws = socket;
        own(() => {
            socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
            if (socket.readyState === WebSocket.OPEN)
                socket.send(new ArrayBuffer(0));
            socket.close();
            ws = null;
        });
        const connectError = await new Promise((resolve) => {
            // Ending the session while the socket is still opening unwinds startup here.
            own(() => resolve({ kind: 'transport', detail: 'cancelled' }));
            socket.onopen = () => {
                socket.send(JSON.stringify({
                    api_key: apiKey,
                    model: SONIOX_DICTATION_MODEL,
                    audio_format: SONIOX_AUDIO_FORMAT,
                    sample_rate: SONIOX_SAMPLE_RATE,
                    num_channels: SONIOX_NUM_CHANNELS,
                    language_hints: ['en'],
                    enable_endpoint_detection: true,
                    max_endpoint_delay_ms: SONIOX_MAX_ENDPOINT_DELAY_MS,
                }));
                logger.info('[soniox] WS connected, config sent', { id });
                resolve(null);
            };
            socket.onerror = () => resolve({ kind: 'transport', detail: 'connect-error' });
            socket.onclose = (evt) => resolve({ kind: 'transport', detail: `closed-before-open-${evt.code}` });
        });
        if (connectError)
            return connectError;
        socket.onmessage = (msg) => {
            if (alive())
                handleSonioxMessage(msg.data);
        };
        // `onclose` carries the code; a separate `onerror` would only duplicate it.
        socket.onclose = (evt) => {
            if (!alive())
                return;
            logger.info('[soniox] WS closed', { id, code: evt.code });
            // A drop while the session is live means Soniox died under us.
            if (startWebSpeech(`ws-closed-${evt.code}`))
                return;
            fail({ kind: 'transport', detail: `ws-closed-${evt.code}` });
        };
        return null;
    };
    /** Microphone -> worklet PCM -> Soniox, with first-frame, dead-chain and silence detection. */
    const startCapture = async (stream) => {
        const audioCtx = new AudioContext();
        own(() => {
            audioCtx.onstatechange = null;
            void audioCtx.close().catch(() => { });
        });
        /** Capture continuations are current only while live and Soniox is still the engine. */
        const current = () => alive() && engine === 'soniox';
        if (audioCtx.state !== 'running') {
            // Never await: autoplay policy can leave this pending until a user gesture.
            // A context that never starts produces no frames, so the no-frames timer fails it.
            void audioCtx.resume().catch(() => { });
        }
        audioCtx.onstatechange = () => {
            if (!current())
                return;
            logger.info('[audio] context state', { id, state: audioCtx.state });
            if (audioCtx.state === 'running' || audioCtx.state === 'closed')
                return;
            void audioCtx.resume().catch(() => {
                if (current())
                    fail({ kind: 'noAudio', detail: 'contextInterrupted' });
            });
        };
        const [track] = stream.getAudioTracks();
        /** Chain diagnostics attached to the first-frame and no-frames logs. */
        const chain = () => ({
            id,
            ctxState: audioCtx.state,
            sampleRate: audioCtx.sampleRate,
            muted: track?.muted ?? null,
            readyState: track?.readyState ?? null,
        });
        if (track) {
            track.onended = () => {
                if (current())
                    fail({ kind: 'noAudio', detail: 'trackEnded' });
            };
            track.onmute = () => {
                if (current())
                    logger.warn('[audio] track muted', { id });
            };
        }
        await audioCtx.audioWorklet.addModule('/audio-worklet.js');
        if (!current())
            return;
        const node = new AudioWorkletNode(audioCtx, 'pcm-worklet-processor');
        own(() => {
            node.port.onmessage = null;
            node.disconnect();
        });
        node.port.onmessage = (evt) => {
            if (!current())
                return;
            if (firstFrameAt === null) {
                firstFrameAt = Date.now();
                logger.info('[audio] first frame', { ...chain(), msSinceStart: firstFrameAt - startedAt });
                // Samples are reaching the recognizer: capture is ready, so the session is active.
                activate();
            }
            if (ws?.readyState === WebSocket.OPEN) {
                ws.send(evt.data.buffer);
                framesSent++;
            }
        };
        const micSource = audioCtx.createMediaStreamSource(stream);
        micSource.connect(node);
        // The worklet emits no audio; connecting it keeps the graph pulled so `process()` runs.
        node.connect(audioCtx.destination);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        micSource.connect(analyser);
        const levels = new Uint8Array(analyser.frequencyBinCount);
        const silence = createSilenceMonitor(config.silenceThreshold, config.silenceDuration);
        let rafId = 0;
        own(() => window.cancelAnimationFrame(rafId));
        // Meters input until the monitor decides (audio heard, or silence reported once).
        const checkLevel = () => {
            if (!current() || silence.done())
                return;
            analyser.getByteFrequencyData(levels);
            const average = levels.reduce((a, b) => a + b, 0) / levels.length;
            if (silence.sample(average, Date.now()))
                config.onSilence?.();
            rafId = window.requestAnimationFrame(checkLevel);
        };
        rafId = window.requestAnimationFrame(checkLevel);
        // Distinguishes "chain alive but silent" (silence toast) from "chain dead" (no frames at all).
        const noFramesTimer = setTimeout(() => {
            if (!current() || firstFrameAt !== null)
                return;
            logger.error('[audio] no frames', chain());
            fail({ kind: 'noAudio', detail: 'noFrames' });
        }, NO_AUDIO_FRAMES_TIMEOUT_MS);
        own(() => clearTimeout(noFramesTimer));
    };
    /**
     * Startup. The microphone comes first so the permission prompt resolves
     * before the Soniox socket opens (Soniox times out a silent socket).
     */
    const run = async () => {
        let stream;
        try {
            stream = await getDefaultAudioStream(await navigator.mediaDevices.enumerateDevices());
        }
        catch (err) {
            if (!alive())
                return;
            const denied = err instanceof Error && PERMISSION_ERROR_NAMES.has(err.name);
            fail(denied ? { kind: 'permissionDenied', detail: String(err) } : startFailed(err));
            return;
        }
        if (!alive()) {
            stopTracks(stream);
            return;
        }
        own(() => stopTracks(stream));
        logger.info('[audio] microphone acquired', { id, msSinceStart: Date.now() - startedAt });
        const connectError = await connectSoniox().catch(startFailed);
        if (!alive())
            return;
        if (connectError && !startWebSpeech('soniox-init-failed')) {
            fail(connectError);
            return;
        }
        if (!connectError) {
            // A Soniox drop during capture setup swaps to Web Speech and closes the
            // context, which can reject a pending capture await; that is not a failure.
            await startCapture(stream).catch((err) => {
                if (engine === 'soniox')
                    throw err;
            });
        }
        // Activation belongs to the engine: Soniox on its first PCM frame, Web Speech in
        // startWebSpeech once `start()` succeeded.
    };
    return {
        id,
        get state() {
            return state;
        },
        start: () => {
            startedAt = Date.now();
            logger.info('[transcription] session started', { id, reason: config.reason });
            transition({ kind: 'connecting' });
            return run().catch((err) => {
                if (alive())
                    fail(startFailed(err));
            });
        },
        stop: () => complete('user'),
    };
};
//# sourceMappingURL=session.js.map